//Hooks
import { get } from 'svelte/store';
//Helpers
import {
	tmp_units_from_settings,
	celsius_to_fahrenheit,
	isThreeEM,
	isDistance,
	isPill,
} from '../../interface_helpers';
import { getTariffByID } from '../../power_prices_helpers';
import { addDate, getUTCOffset } from '../../date_helper';
import { convertDistanceChartUnits } from 'src/helpers/distance_helper';
import { convertNeoValveChartUnits } from 'src/helpers/xt1_helpers/template_units_helpers';
import { calculateNetMetering } from '../core/net_metering/chart_net_metering_helpers';
import { isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
import { convertCost, convertToValueAndUnit } from 'src/helpers/units_helpers';
import { TEMPERATURE_SENSORS_IN_CELSIUS } from 'src/constants';
import { user_settings, devices_settings } from 'src/stores';
import {
	getTotalConsumption,
	getTotalReturnedConsumption,
} from '../core/consumptions/consumption_helpers';
import { isBleStatisticsDeviceToUTC } from '../ui/chart_control_helpers';
import { convertPrecipitationFromMm } from 'src/helpers/units/precipitation_unit';
import { isPillTemperatureMeasurement } from 'src/helpers/thepill/thepill_data_helpers';
import { convertWindSpeedFromMs, windSpeedUnitLabel } from 'src/helpers/units/wind_units';
import { isDrawableStatisticSample } from './chart_data_validity';
//Types
import type { Device } from 'src/types/Device';
import type {
	ChartConfig,
	ChartDataInterface,
	ChartStatisticRangePeriod,
	ConsumptionDataset,
} from 'src/types/Chart';
import type {
	GetStatsResponseData,
	GetStatsResponseHistoryData,
} from 'src/types/requests/charts/get_stats_period';
import type { WindSpeedUnit } from 'src/helpers/units/wind_units';

export const parseStatisticsData = (
	data: GetStatsResponseData,
	chartConfig: ChartConfig,
	rangePeriod: ChartStatisticRangePeriod,
	device: Device = null,
	prevPeriodData = null,
	dummyNetMetering: boolean = false,
	period: ChartStatisticRangePeriod | null = null,
): ChartDataInterface => {
	if (isXT1Module(chartConfig?.deviceType)) {
		convertNeoValveChartUnits(data, chartConfig);
	} else if (isDistance(chartConfig?.deviceType)) {
		convertDistanceChartUnits(data, chartConfig);
	}
	const chartData: ChartDataInterface = {
		...chartConfig,
		rangePeriod,
		rangeInterval: data.history_interval || data.interval,
		series: [],
	};
	const series = chartConfig.series || [],
		threeEM = isThreeEM(device?.type) && device?.mode != 'monophase',
		currency = get(user_settings).currency || '';
	let totalConsumption = 0,
		totalConsumptionCost = 0,
		totalReversedConsumption = 0;

	const settings = devices_settings.getByRealId(device?.id);
	const isPillTempMeasurement = isPillTemperatureMeasurement(
		chartConfig,
		chartData.units,
		device?.type,
	);
	const isGenericTempMeasurement =
		chartConfig.name === 'measurement' && chartConfig.title === 'temperature';
	if (chartConfig.name == 'temperature' || isPillTempMeasurement || isGenericTempMeasurement) {
		chartData.units = tmp_units_from_settings(
			TEMPERATURE_SENSORS_IN_CELSIUS.includes(device.type) ? device : settings,
		);
	}
	for (let i = 0; i < series.length; ++i) {
		const item = series[i],
			historyData = item?.prevPeriod ? prevPeriodData : data,
			reversed = /(returned|reversed)/.test(item.name),
			history = threeEM
				? /total/.test(item.name)
					? historyData?.[item.index]
					: historyData?.history[item.index]
				: historyData?.history;

		chartData.series[i] = { ...item, data: [] };
		if (!history) continue;
		let consumptionDataset: ConsumptionDataset = {};
		let seriesMissingData = true;
		const chartName = threeEM
				? chartData.name == 'voltage'
					? item.name.substring(8)
					: chartConfig.name
				: item.name,
			estimateConsumption = /^(total|consumption)$/.test(item.name) && !item.prevPeriod,
			reversedConsumption = /^(reversed)$/.test(item.name) && !item.prevPeriod;
		history.map((obj: GetStatsResponseHistoryData) => {
			if (seriesMissingData && !obj?.missing) {
				seriesMissingData = false;
			}
			let value: number = obj?.missing ? 0 : reversed ? obj.reversed : obj[chartName],
				parts = obj.datetime.split(/[-\s:T]/),
				dt = Date.UTC(
					Number(parts[0]),
					Number(parts[1]) - 1,
					Number(parts[2]),
					Number(parts[3]),
					Number(parts[4]),
					0,
					0,
				);
			if (isBleStatisticsDeviceToUTC(device, period)) {
				const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
				const offsetHours = getUTCOffset(timezone).hours;
				dt = addDate(dt, 'h', offsetHours).getTime();
			}
			if (!/consumption|voltage/i.test(chartConfig.name)) {
				if (!isDrawableStatisticSample(obj?.missing, value)) {
					return;
				}
				if (
					chartData.units === '°F' &&
					(chartConfig.name === 'temperature' ||
						isPillTempMeasurement ||
						isGenericTempMeasurement)
				) {
					value = celsius_to_fahrenheit(value);
				} else if (/^(precipitation)$/.test(chartConfig.name)) {
					const unit = device?.ht_ui?.precipitation_unit || 'mm';
					chartData.units = unit;
					value = convertPrecipitationFromMm(value, unit);
				} else if (chartConfig.name === 'wind_speed') {
					const unit = (device?.ht_ui?.wind_unit as WindSpeedUnit) || 'ms';
					chartData.units = windSpeedUnitLabel(unit);
					value = convertWindSpeedFromMs(value, unit);
				}
				chartData.series[i].data.push({
					dt,
					value,
					name: item.name,
					bgColor: undefined,
				});
			} else {
				let costPower = null;
				let rawCost = obj?.missing ? 0 : obj?.cost;
				if (dummyNetMetering && !reversed && chartConfig.name != 'voltage') {
					let rawValue = value;
					value = Math.max(value - (obj?.reversed || 0), 0);
					if (rawCost) {
						const costPerUnit = (1000 / rawValue) * rawCost;
						rawCost = (value / 1000) * costPerUnit;
					}
				}
				if (reversed) {
					if (dummyNetMetering && value) {
						value = Math.max((obj?.reversed || 0) - (obj?.consumption || 0), 0);
					}
					value = value ? value * -1 : 0;
				} else if (rawCost) {
					const cost = convertCost(rawCost, currency),
						tariffID = obj.tariff_id || '0',
						tariffName = !device?.shared && getTariffByID(tariffID)?.name,
						unit = 'Wh',
						power = convertToValueAndUnit(value, unit);
					costPower = {
						tariff: {
							id: tariffID,
							name: (tariffID != '0' && tariffName) || `Tariff ${tariffID}`,
							cost: rawCost,
							value,
							currency,
							unit,
						},
						cost,
						value: `${power.value} ${power.unit}`,
						show: estimateConsumption,
					};
				}

				if (!consumptionDataset[dt]) {
					consumptionDataset[dt] = {
						dt,
						name: item.name,
						value: value || 0,
						missing: Boolean(obj?.missing),
						active_net: obj.active_net,
						datetime: obj.datetime,
						reversed,
					};
					if (!reversed && chartConfig.name != 'voltage') {
						consumptionDataset[dt].cost = rawCost || 0;
						consumptionDataset[dt].metaData = [costPower];
					}
				} else if (chartConfig.name == 'voltage') {
					consumptionDataset[dt].value = item.name.startsWith('min')
						? Math.min(consumptionDataset[dt].value, value) || 0
						: Math.max(consumptionDataset[dt].value || 0, value || 0);
				} else {
					consumptionDataset[dt].value += value;
					if (obj?.missing && !consumptionDataset[dt].missing) {
						consumptionDataset[dt].missing = true;
					}
					if (!reversed) {
						consumptionDataset[dt].cost += rawCost || 0;
						consumptionDataset[dt].metaData.push(costPower);
					}
				}
			}
		});
		if (!/consumption|voltage/i.test(chartConfig.name)) {
			chartData.series[i].missingData = seriesMissingData || chartData.series[i].data.length === 0;
		}
		if (Object.keys(consumptionDataset).length) {
			consumptionDataset = calculateNetMetering(consumptionDataset);
			Object.keys(consumptionDataset)
				.sort()
				.forEach((key: string) => {
					const e = consumptionDataset[key];
					if (estimateConsumption) {
						totalConsumption += e.value;
						totalConsumptionCost += e.cost;
					} else if (reversedConsumption) {
						totalReversedConsumption += e.value;
					}
					chartData.series[i].data.push({
						...e,
						prevValue: item?.prevPeriod && prevPeriodData ? e.dt : null,
						convertCost: convertCost(e.cost, currency),
					});
				});
			chartData.series[i].missingData = seriesMissingData;
		}
	}
	if (/consumption/i.test(chartConfig.name)) {
		chartData.totalConsumption = getTotalConsumption(totalConsumption, totalConsumptionCost);
		chartData.totalReturnedConsumption = getTotalReturnedConsumption(totalReversedConsumption);
		chartData.devices = data.devices;
		chartData.totalRoomConsuption = totalConsumption;
		chartData.totalReversedConsumption = totalReversedConsumption;
	}
	chartData.timezone = data.timezone;
	return chartData;
};
